import request, {requestBinary, requestWithoutAuth, uploadFile} from "./requests";
import moment from "moment";

const getFileNameFromDisposition = (contentDisposition, fallback) => {
    if (!contentDisposition) {
        return fallback;
    }

    const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch?.[1]) {
        return decodeURIComponent(utfMatch[1]);
    }

    const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    if (asciiMatch?.[1]) {
        return asciiMatch[1];
    }

    return fallback;
}

export var SERVER = () => {
    if (process.env.NODE_ENV === 'development') {
        return 'http://localhost:8081'
    } else {
        return 'https://band-api.avocados.ovh'
    }
}

export const getHealth = async () => {
    let data = (await requestWithoutAuth(`${SERVER()}/actuator/health`, {method: 'GET'})).data
    console.log(data)
    return data
}

export const getCurrentUser = async () => {
    return (await request(`${SERVER()}/api/users/me`, {method: 'GET'})).data
}

export const getCurrentSpace = async (spaceId) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}`, {method: 'GET'})).data
}

export const getCurrentSpaceStatistics = async (spaceId) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/statistics`, {method: 'GET'})).data
}

export const getAllRecordings = async (spaceId) => {
    const response = await request(`${SERVER()}/api/spaces/${spaceId}/recordings`, {method: 'GET'})
    const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data
    return Array.isArray(data) ? data : []
}

export const getAllCommentThreads = async (spaceId, recordingId) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/recordings/${recordingId}/threads`, {method: 'GET'})).data
}

export const createCommentThread = async (spaceId, recordingId, data) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/recordings/${recordingId}/threads`, {
        method: 'POST',
        body: data
    })).data
}

export const getAllLabels = async (spaceId) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/labels`, {method: 'GET'})).data
}

export const getLabelsWithCounts = async (spaceId) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/labels/with-counts`, {method: 'GET'})).data
}

export const deleteLabel = async (spaceId, labelId, reassignToLabelId) => {
    let url = `${SERVER()}/api/spaces/${spaceId}/labels/${labelId}`;
    if (reassignToLabelId) {
        url += `?reassignTo=${reassignToLabelId}`;
    }
    return (await request(url, {method: 'DELETE'})).data
}

export const getAllAvailabilities = async (spaceId) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/availabilities`, {method: 'GET'})).data
}

export const getAllRoomBookings = async (spaceId) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/room-bookings`, {method: 'GET'})).data
}

export const deleteAvailability = async (spaceId, availabilityId) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/availabilities/${availabilityId}`, {method: 'DELETE'})).data
}

export const createAvailability = async (spaceId, data) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/availabilities`, {
        method: 'POST',
        body: data
    })).data
}

export const getAllSpaces = async () => {
    const response = await request(`${SERVER()}/api/spaces`, {method: 'GET'})
    const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data
    return Array.isArray(data) ? data : []
}

export const createSpace = async (data) => {
    return (await request(`${SERVER()}/api/spaces`, {method: 'POST', body: data})).data
}

export const updateSpace = async (spaceId, data) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}`, {method: 'PATCH', body: data})).data
}

export const createLabel = async (spaceId, data) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/labels`, {method: 'POST', body: data})).data
}

export const createRecording = async (spaceId, file, onProgress) => {
    console.log(file)
    return (await request(`${SERVER()}/api/spaces/${spaceId}/recordings`, {
        method: 'POST',
        body: {
            file_name: file.name,
            created_at: moment(file.lastModified).toISOString()
        }
    }).then(req => uploadFile(req.data, file, onProgress))).data
}

export const updateRecording = async (spaceId, data) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/recordings/${data.recording_id}`, {
        method: 'PATCH',
        body: data
    })).data
}

export const bulkUpdateRecordings = async (spaceId, recordingIds, songId) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/recordings/bulk`, {
        method: 'PATCH',
        body: { recordingIds, songId }
    })).data
}

export const listAvailableJobs = async (spaceId) => {
    const response = (await request(`${SERVER()}/api/spaces/${spaceId}/jobs`, {method: 'GET'})).data;
    console.log('listAvailableJobs response:', response);
    return response;
}

export const getJobSchedule = async (spaceId, jobType) => {
    if (jobType === 'NOTIFICATION_DIGEST') {
        return (await request(`${SERVER()}/api/spaces/${spaceId}/jobs/notification-digest/schedule`, {method: 'GET'})).data
    } else if (jobType === 'WEEKLY_AVAILABILITY') {
        return (await request(`${SERVER()}/api/spaces/${spaceId}/jobs/weekly-availability/schedule`, {method: 'GET'})).data
    }
    return (await request(`${SERVER()}/api/spaces/${spaceId}/jobs/${jobType}/schedule`, {method: 'GET'})).data
}

export const createOrUpdateJobSchedule = async (spaceId, jobType, data) => {
    console.log('====== createOrUpdateJobSchedule API called ======');
    let url;
    if (jobType === 'NOTIFICATION_DIGEST') {
        url = `${SERVER()}/api/spaces/${spaceId}/jobs/notification-digest/schedule`;
        // Convert data format for notification digest
        data = { frequency: data.frequency, enabled: data.enabled };
    } else if (jobType === 'WEEKLY_AVAILABILITY') {
        url = `${SERVER()}/api/spaces/${spaceId}/jobs/weekly-availability/schedule`;
        // Convert data format for weekly availability
        data = { day: data.day, enabled: data.enabled };
    } else {
        url = `${SERVER()}/api/spaces/${spaceId}/jobs/${jobType}/schedule`;
    }
    console.log('URL:', url);
    console.log('Data:', data);
    try {
        const result = await request(url, {
            method: 'POST',
            body: data
        });
        console.log('API response received:', result);
        return result.data;
    } catch (error) {
        console.error('API request failed:', error);
        throw error;
    }
}

export const deleteJobSchedule = async (spaceId, jobType) => {
    if (jobType === 'NOTIFICATION_DIGEST') {
        return (await request(`${SERVER()}/api/spaces/${spaceId}/jobs/notification-digest/schedule`, {method: 'DELETE'})).data
    } else if (jobType === 'WEEKLY_AVAILABILITY') {
        return (await request(`${SERVER()}/api/spaces/${spaceId}/jobs/weekly-availability/schedule`, {method: 'DELETE'})).data
    }
    return (await request(`${SERVER()}/api/spaces/${spaceId}/jobs/${jobType}/schedule`, {method: 'DELETE'})).data
}

export const triggerJobExecution = async (spaceId, jobType) => {
    try {
        return (await request(`${SERVER()}/api/spaces/${spaceId}/jobs/${jobType}/executions`, {method: 'POST'})).data;
    } catch (error) {
        console.error('Trigger API request failed:', error);
        throw error;
    }
}

export const getJobExecutionHistory = async (spaceId, jobType, limit = 20, offset = 0) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/jobs/${jobType}/executions?limit=${limit}&offset=${offset}`, {method: 'GET'})).data
}

export const getAllSongs = async (spaceId) => {
    const response = await request(`${SERVER()}/api/spaces/${spaceId}/songs`, {method: 'GET'})
    const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data
    return Array.isArray(data) ? data : []
}

export const createSong = async (spaceId, data) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/songs`, {method: 'POST', body: data})).data
}

export const updateSong = async (spaceId, songId, data) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/songs/${songId}`, {method: 'PATCH', body: data})).data
}

export const deleteSong = async (spaceId, songId) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/songs/${songId}`, {method: 'DELETE'})).data
}

export const getPartitionUploadUrl = async (spaceId, songId, fileName, fileFormat) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/songs/${songId}/partition-upload-url?fileName=${encodeURIComponent(fileName)}&fileFormat=${encodeURIComponent(fileFormat)}`, {method: 'POST'})).data
}

export const getLyricsUploadUrl = async (spaceId, songId, fileName) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/songs/${songId}/lyrics-upload-url?fileName=${encodeURIComponent(fileName)}`, {method: 'POST'})).data
}

export const addPartition = async (spaceId, songId, fileName, format) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/songs/${songId}/partition`, {method: 'POST', body: {file_name: fileName, format}})).data
}

export const addLyrics = async (spaceId, songId, fileName, language = null) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/songs/${songId}/lyrics`, {method: 'POST', body: {file_name: fileName, language}})).data
}

export const deletePartition = async (spaceId, partitionId) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/songs/partition/${partitionId}`, {method: 'DELETE'})).data
}

export const deleteLyrics = async (spaceId, lyricsId) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/songs/lyrics/${lyricsId}`, {method: 'DELETE'})).data
}

export const getPartitionDownloadUrl = async (spaceId, songId, partitionId) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/songs/${songId}/partition/${partitionId}/download-url`, {method: 'GET'})).data
}

export const downloadPartitionFile = async (spaceId, songId, partitionId, format = 'original') => {
    const response = await requestBinary(
        `${SERVER()}/api/spaces/${spaceId}/songs/${songId}/partition/${partitionId}/download?format=${encodeURIComponent(format)}`,
        {method: 'GET'}
    );

    return {
        blob: response.data,
        fileName: getFileNameFromDisposition(response.headers['content-disposition'], `partition.${format}`),
        contentType: response.headers['content-type'] || 'application/octet-stream'
    }
}

export const getPartitionRenderConfig = async (spaceId, songId, partitionId) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/songs/${songId}/partition/${partitionId}/render-config`, {method: 'GET'})).data
}

export const getLyricsDownloadUrl = async (spaceId, songId, lyricsId) => {
    return (await request(`${SERVER()}/api/spaces/${spaceId}/songs/${songId}/lyrics/${lyricsId}/download-url`, {method: 'GET'})).data
}

export const downloadRenderedPartitionFile = async (spaceId, songId, partitionId, options = {}) => {
    const response = await requestBinary(
        `${SERVER()}/api/spaces/${spaceId}/songs/${songId}/partition/${partitionId}/rendered-download`,
        {method: 'POST', body: options, headers: {'Content-Type': 'application/json'}}
    );

    return {
        blob: response.data,
        fileName: getFileNameFromDisposition(response.headers['content-disposition'], `partition.pdf`),
        contentType: response.headers['content-type'] || 'application/pdf'
    }
}
